import { Search } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useAdminResource } from '../useAdminResource'
import { money, formatDate } from '../format'
import type { AdminCustomerPage } from '../types'

const PAGE_SIZE = 50

export function AdminCustomers() {
  const [search, setSearch] = useState('')
  const [applied, setApplied] = useState('')
  const [offset, setOffset] = useState(0)
  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset), q: applied })
  const { data: page, error, busy } = useAdminResource<AdminCustomerPage>(`/api/admin/customers?${params}`)

  const submit = (event: FormEvent) => { event.preventDefault(); setOffset(0); setApplied(search.trim()) }
  const shownTo = page ? page.offset + page.customers.length : 0

  return (
    <section className="admin-orders page page--staff">
      <header className="page-head">
        <div>
          <div className="eyebrow"><span aria-hidden="true" />Admin</div>
          <h1>Customers</h1>
          <p>Everyone who has placed an order, most recently active first. Lifetime figures include archived history and both shipping legs; test payments are excluded.</p>
        </div>
      </header>

      <form className="admin-filters" onSubmit={submit}>
        <div className="admin-search">
          <Search size={17} aria-hidden="true" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Name or email" aria-label="Search customers" />
          <button type="submit" className="button button--dark">Search</button>
        </div>
      </form>

      {error && <div className="form-error" role="alert">{error}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Customer</th><th className="num">Orders</th><th className="num">Authorized</th>
              <th className="num">Lifetime charges</th><th className="num">Refunded</th>
              <th>Last order</th><th>Account since</th>
            </tr>
          </thead>
          <tbody>
            {page?.customers.map(c => (
              <tr key={c.userId}>
                <td>
                  <Link className="admin-order-link" to={`/admin/customers/${c.userId}`}>{c.fullName || '—'}</Link>
                  <small>{c.email}</small>
                </td>
                <td className="num">{c.orderCount}</td>
                <td className="num">{c.authorizedCount}</td>
                <td className="num">{money(c.lifetimeGrossMinor)}</td>
                <td className={`num ${c.refundedMinor ? 'admin-neg' : ''}`}>{c.refundedMinor ? money(c.refundedMinor) : '—'}</td>
                <td>{c.lastOrderAt ? formatDate(c.lastOrderAt) : '—'}</td>
                <td>{formatDate(c.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!busy && page?.customers.length === 0 && (
          <p className="admin-empty">{applied ? 'No customers match that search.' : 'No customers have ordered yet.'}</p>
        )}
        {busy && <p className="admin-empty">Loading customers…</p>}
      </div>

      {page && page.total > 0 && (
        <nav className="admin-paging" aria-label="Pagination">
          <span>Showing {page.offset + 1}–{shownTo} of {page.total}</span>
          <button type="button" className="button button--outline" disabled={page.offset === 0}
            onClick={() => setOffset(Math.max(0, page.offset - PAGE_SIZE))}>Previous</button>
          <button type="button" className="button button--outline" disabled={shownTo >= page.total}
            onClick={() => setOffset(page.offset + PAGE_SIZE)}>Next</button>
        </nav>
      )}
    </section>
  )
}
